import { DailyWorkRepository, DailyWorkJoin, DailyWorkDayJoin } from "@/repositories/daily-work.repository";
import type { WorkDayCreateRequest, WorkDayDetailsRequest } from "@shyne/shared/api";
import { DailyWorkExecutionRepository, DailyWorkExecutionJoin } from "@/repositories/daily-work-execution.repository";
import { DailyWorkLogJoin } from "@/repositories/daily-work.repository";
import type { ContractDetailsChange, ContractListFilters, NewContractRecord } from "@/repositories/contract-records";

export async function listDailyWorkForVendor(
  page: number,
  pageSize: number,
  filters: ContractListFilters,
  sortKey: string,
  sortOrder: "asc" | "desc"
) {
  return DailyWorkRepository.listForVendor(page, pageSize, filters, sortKey, sortOrder);
}

export async function getDailyWork(id: string): Promise<DailyWorkJoin> {
  return DailyWorkRepository.getById(id);
}

export async function createDailyWork(payload: NewContractRecord): Promise<DailyWorkJoin> {
  return DailyWorkRepository.create(payload);
}

export async function updateDailyWork(id: string, payload: ContractDetailsChange): Promise<DailyWorkJoin> {
  return DailyWorkRepository.update(id, payload);
}

export async function getDailyWorkLogs(id: string): Promise<DailyWorkLogJoin[]> {
  return DailyWorkRepository.listLogs(id);
}

export async function markDailyWorkDayOffSiteByVendor(
  day: DailyWorkDayJoin,
  input: { clockOut: string; note?: string | null }
): Promise<{ day: DailyWorkDayJoin; execution: DailyWorkExecutionJoin | undefined }> {
  const closed = await DailyWorkRepository.markOffSite(day.id, { clockOut: input.clockOut, note: input.note ?? null });
  return { day: closed.day, execution: closed.clockRecords[0] };
}

export async function getDailyWorkDays(dailyWorkId: string, startDate?: string, endDate?: string): Promise<DailyWorkDayJoin[]> {
  return DailyWorkRepository.getDaysByDailyWork(dailyWorkId, startDate, endDate);
}

export async function createDailyWorkDay(contract: string, day: Omit<WorkDayCreateRequest, "actingOrganization">): Promise<DailyWorkDayJoin> {
  return DailyWorkRepository.addDay(contract, day);
}

export async function updateDailyWorkDay(id: string, change: Omit<WorkDayDetailsRequest, "actingOrganization">): Promise<DailyWorkDayJoin> {
  return DailyWorkRepository.changeDay(id, change);
}

export async function resolveDailyWorkPayoutHours(
  id: string,
  hoursWorked: number
): Promise<DailyWorkDayJoin> {
  return DailyWorkRepository.resolvePayoutHours(id, hoursWorked);
}

export async function deleteDailyWorkDay(id: string): Promise<void> {
  return DailyWorkRepository.deleteDay(id);
}

export async function listDailyWorkExecutionsByDay(dailyWorkDayId: string): Promise<DailyWorkExecutionJoin[]> {
  return DailyWorkExecutionRepository.listByDay(dailyWorkDayId);
}

export async function getVendorProperties(): Promise<{ id: string; name: string; address?: string }[]> {
  const { store } = await import("@/lib/store");
  const { PropertyRepository } = await import("@/repositories/properties.repository");
  const vendorOrgId = store.getState().organization.activeOrgId;
  const properties = await PropertyRepository.servicedBy(vendorOrgId);
  return properties.map(({ id, name, address }) => ({ id, name, address }));
}
